import React, { FC, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, Card, Image, List, message, Popconfirm, Space, Tag, Typography } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'
import { translate } from '../../../core'
import { resolvePathFile } from '../../../core/until'
import { InputCard } from '../../inventory/component/InputCard'
import {
  DraggableList,
  DraggableListItem,
  DragListItemHandle,
} from '../../../core/component'
import {
  FindCarouselSlides,
  DeleteCarouselSlide,
  ReorderCarouselSlides,
} from '../graphql/carousel.graphql'
import { CarouselGroup, CarouselMainSlide } from '../type/carousel'
import { CarouselMainSlideForm } from './CarouselMainSlideForm'

type Props = {
  group: CarouselGroup
}

export const CarouselMainSlideList: FC<Props> = ({ group }) => {
  const [data, setData] = useState<CarouselMainSlide[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [editingSlide, setEditingSlide] = useState<CarouselMainSlide | undefined>()
  const [messageApi, contextHolder] = message.useMessage()

  const { data: queryData, loading, refetch } = useQuery(FindCarouselSlides, {
    variables: { group },
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    if (queryData?.carouselSlidesFind) {
      setData(
        queryData.carouselSlidesFind.map((it) => ({
          ...it,
          key: it.id.toString(),
        })),
      )
    }
  }, [queryData])

  const [deleteSlide] = useMutation(DeleteCarouselSlide, {
    onCompleted: () => {
      messageApi.success(translate('carousel', 'deleted_success_message'))
      refetch()
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [reorder] = useMutation(ReorderCarouselSlides, {
    onCompleted: () => {
      messageApi.success(translate('update_priority_success_message'))
      refetch()
    },
    onError: (err) => messageApi.error(err.message),
  })

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id === over?.id) return

    setData((prev) => {
      const activeIndex = prev.findIndex((r) => r.key === active?.id)
      const overIndex = prev.findIndex((r) => r.key === over?.id)
      const newItems = arrayMove(prev, activeIndex, overIndex)

      reorder({
        variables: {
          input: newItems.map(({ id }, index) => ({
            id,
            displayOrder: index,
          })),
        },
      })

      return newItems
    })
  }

  const handleAdd = () => {
    setEditingSlide(undefined)
    setFormOpen(true)
  }

  const handleEdit = (slide: CarouselMainSlide) => {
    setEditingSlide(slide)
    setFormOpen(true)
  }

  const handleFormClose = (saved?: boolean) => {
    setFormOpen(false)
    setEditingSlide(undefined)
    if (saved) refetch()
  }

  const entityLabel = group === CarouselGroup.Watch
    ? translate('brand')
    : translate('category')

  return (
    <InputCard contained>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          {translate('add')} {translate('slide')}
        </Button>
      </Space>

      <DraggableList items={data} onDragEnd={onDragEnd}>
        <List
          loading={loading}
          dataSource={data}
          renderItem={(slide) => (
            <DraggableListItem key={slide.key} id={slide.key}>
              <Card
                size="small"
                style={{ marginBottom: 8 }}
                bodyStyle={{ display: 'flex', alignItems: 'center', gap: 16 }}
              >
                <DragListItemHandle />
                <Image
                  width={80}
                  height={50}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  src={resolvePathFile(slide.mainImage)}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                  preview={false}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text strong ellipsis>
                    {slide.title}
                  </Typography.Text>
                  {slide.subtitle && (
                    <Typography.Text
                      type="secondary"
                      ellipsis
                      style={{ display: 'block', fontSize: 12 }}
                    >
                      {slide.subtitle}
                    </Typography.Text>
                  )}
                </div>
                <Tag>{entityLabel}: #{slide.entityId}</Tag>
                <Tag color="blue">
                  {slide.subSlides?.length ?? 0} {translate('sub_slides')}
                </Tag>
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(slide)}
                  />
                  <Popconfirm
                    title={translate('sure_message')}
                    onConfirm={() => deleteSlide({ variables: { id: slide.id } })}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </Card>
            </DraggableListItem>
          )}
        />
      </DraggableList>

      {formOpen && (
        <CarouselMainSlideForm
          group={group}
          slide={editingSlide}
          onClose={handleFormClose}
        />
      )}
      {contextHolder}
    </InputCard>
  )
}
